/**
 * PURPOSE: Renderizar la navegación principal en tema claro y el acceso ADMIN a actividad reciente.
 * RESPONSIBILITIES: Gestionar año, menú responsive, contador de auditoría y apertura del panel de notificaciones.
 * COLLABORATORS: AuditNotificationPanel.jsx, auditNotificationService.js, Redux y yearSlice.js.
 * ROUTE: winscore/src/components/global/Navbar/Navbar.jsx
 */
import React, { useEffect, useState } from "react";
import { FaBell } from "react-icons/fa";
import { HiOutlineMenuAlt2 } from "react-icons/hi";
import { useDispatch, useSelector } from "react-redux";
import { setYearSelected } from "../../../features/yearSlice.js";
import { AuditNotificationPanel } from "../AuditNotificationPanel/AuditNotificationPanel.jsx";
import { getAuditUnreadCount } from "../../../services/auditNotificationService.js";

const Navbar = ({ toggleSidebar }) => {
  const [years, setYears] = useState([]);
  const [selectedYear, setSelectedYear] = useState(null);
  const dispatch = useDispatch();
  const user = useSelector((state) => state.auth.user);
  const [auditOpen, setAuditOpen] = useState(false);
  const [auditUnread, setAuditUnread] = useState(0);
  const isAuditAdmin = Number(user?.rol) === 1;

  useEffect(() => {
    const fetchYears = async () => {
      try {
        const response = await fetch("https://cloud-master.perufedup.com/public/catalogue/MjA=");
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data = await response.json();

        // Ordenar años en orden descendente
        const sortedYears = data.sort((a, b) => parseInt(b.name) - parseInt(a.name));
        setYears(sortedYears);

        // Seleccionar el año más reciente como predeterminado
        const defaultYear = sortedYears[0];
        setSelectedYear(defaultYear?.id);
        dispatch(setYearSelected(defaultYear?.id));
      } catch (error) {
        console.error("Error fetching years:", error);
      }
    };

    fetchYears();
  }, [dispatch]);


  useEffect(() => {
    if (!isAuditAdmin) return undefined;
    let active = true;
    const refresh = async () => {
      try {
        const payload = await getAuditUnreadCount();
        if (active) setAuditUnread(Number(payload?.count || 0));
      } catch (_error) {
        if (active) setAuditUnread(0);
      }
    };
    refresh();
    const interval = window.setInterval(refresh, 60000);
    window.addEventListener("focus", refresh);
    return () => { active = false; window.clearInterval(interval); window.removeEventListener("focus", refresh); };
  }, [isAuditAdmin]);

  const handleYearChange = (e) => {
    const year = e.target.value;
    setSelectedYear(year);
    dispatch(setYearSelected(year));
  };

  return (
    <nav className="fixed top-0 z-50 w-full bg-white border-b border-gray-200 dark:bg-gray-800 dark:border-gray-700">
      <div className="px-3 py-3 lg:px-5 lg:pl-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center justify-start rtl:justify-end">
            <button
              className="inline-flex items-center p-2 text-sm text-gray-500 rounded-lg sm:hidden hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-200 dark:text-gray-400 dark:hover:bg-gray-700 dark:focus:ring-gray-600"
              onClick={toggleSidebar}
            >
              <HiOutlineMenuAlt2 className="text-2xl" />
            </button>
            <a href="#" className="flex ms-2 md:me-24">
              <div>
                <img style={{ width: "32px" }} src="/assets/logos/logo3.png" alt="" />
              </div>
              <span className="self-center text-xl font-semibold sm:text-2xl whitespace-nowrap dark:text-white">
                INSCORE
              </span>
            </a>
          </div>

          <div className="flex items-center space-x-4">
            <label className="hidden md:block text-gray-700 dark:text-white text-sm font-medium">
              Periodo:
            </label>
            <select
              value={selectedYear || ""}
              onChange={handleYearChange}
              className="bg-gray-50 border border-gray-300 text-gray-700 text-sm rounded-lg focus:ring-2 focus:ring-blue-400 focus:border-blue-400 dark:bg-gray-800 dark:border-gray-600 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500 py-2 px-4"
            >
              {years.map((year) => (
                <option key={year.id} value={year.id}>
                  {year.name}
                </option>
              ))}
            </select>
            {isAuditAdmin ? (
              <button
                type="button"
                onClick={() => setAuditOpen(true)}
                className="relative rounded-full p-2 text-gray-600 transition hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-gray-700"
                aria-label="Abrir actividad reciente"
              >
                <FaBell className="text-lg" />
                {auditUnread > 0 ? (
                  <span className="absolute -right-1 -top-1 min-w-5 rounded-full bg-red-600 px-1.5 py-0.5 text-center text-[0.65rem] font-black text-white">
                    {auditUnread > 99 ? "99+" : auditUnread}
                  </span>
                ) : null}
              </button>
            ) : null}
          </div>
        </div>
      </div>
      <AuditNotificationPanel open={auditOpen} onClose={() => setAuditOpen(false)} onRead={() => setAuditUnread(0)} />
    </nav>
  );
};

export default Navbar;
